// Canvas v2 state. Lives on the root reactive store so prop functions
// re-evaluate on mutation. Writes funnel through `patchCanvas` so the
// backing store can later swap to project.settings.canvas without
// touching components.

import { resolveTopWindow, HANDLE_NAMES } from '../../globalScope.js'

const DEFAULTS = () => ({
  viewport: { x: 40, y: 40, scale: 0.35 },
  items: {},
  groups: {},
  guides: { horizontal: [], vertical: [] },
  selection: { ids: [], primary: null },
  marquee: null,
  mode: 'default',
  snap: {
    enabled: true,
    threshold: 6,       // screen px tolerance
    toGrid: true,       // grid snap ON by default
    gridStep: 64,       // canvas-space px
    toItems: true,
    toGuides: true
  },
  // Per project_canvas_ds_fetch_fix.md: per-tile iframes are the proven
  // path — they isolate scratch's module-level FACTORY and give the
  // page its own state tree (so fetch queries that need projectId etc.
  // work correctly). Inline mounting is the lighter-weight default —
  // `?frameMode=1` opts into per-tile iframes for full project isolation.
  // Visual connections between items — arrows from one item to another.
  // Stored as `[{ from: pageKey, to: pageKey, label?: string }]`. Rendered
  // by `refreshCanvasChrome` into the `CvConnectors` SVG layer.
  connections: [],
  // Off by default. Toggled from the settings modal.
  showRulers: false,
  showMinimap: false,
  frameMode: (() => {
    try {
      if (typeof window === 'undefined') return false
      const sp = new URLSearchParams(window.location.search)
      if (sp.get('frameMode') === '1') return true
      return false
    } catch { return false }
  })(),
  settingsOpen: false,
  session: null
})

function rootState(el) {
  return el?.state?.root || el?.getRootState?.() || el?.getRoot?.()?.state
}

const STORAGE_KEY = 'cv2_settings'
function loadStored() {
  try { return JSON.parse(window.localStorage?.getItem(STORAGE_KEY) || '{}') } catch { return {} }
}
function persistSettings(canvas) {
  try {
    const slim = {
      snap: canvas.snap,
      frameMode: canvas.frameMode,
      showRulers: canvas.showRulers,
      showMinimap: canvas.showMinimap
    }
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(slim))
  } catch {}
}

export function patchCanvas(patch, opts) {
  const root = rootState(this)
  if (!root?.update) return
  const prev = root.canvas || DEFAULTS()
  const next = { ...prev, ...patch }
  root.update({ canvas: next }, opts)
  if ('snap' in patch || 'frameMode' in patch || 'showRulers' in patch || 'showMinimap' in patch) persistSettings(next)
}

// Single helper for snap field toggles — replaces the inline
// `{...v2.snap, X: y}` spread that 6 settings handlers were doing.
export function patchSnap(patch) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  this.call('patchCanvas', { snap: { ...v2.snap, ...patch } })
}

// Direct mutation for high-frequency commits (drag/resize/nudge). A
// state.update here would rebuild every CanvasItem and remount any
// iframe children; the style was already written imperatively by the
// gesture handler.
export function patchCanvasItem(key, itemPatch) {
  const root = rootState(this)
  const canvas = root?.canvas
  if (!canvas || !key) return
  canvas.items = canvas.items || {}
  canvas.items[key] = { ...(canvas.items[key] || {}), ...itemPatch }
}

// Debounced project write. The `canvas` field is a recognized
// contentField — the server stores it in the ProjectData document and
// includes it in subsequent project reads. `settings` is also a
// contentField but many project reads strip it, so we keep the layout
// at the top level to match Workspace's `canvasPosition` pattern.
let _persistTimer = null
export function persistCanvasLayout() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const ctx = this.context || this.getRootContext?.() || {}
  const sdk = ctx.methods?.sdk
  if (!sdk?.isReady?.()) return
  const data = rootState(this)?.data
  const projectId = data?._id
  if (!projectId) return
  const payload = { items: v2.items, guides: v2.guides, groups: v2.groups, viewport: v2.viewport }

  if (_persistTimer) clearTimeout(_persistTimer)
  _persistTimer = setTimeout(async () => {
    _persistTimer = null
    try {
      await sdk.updateProject(projectId, { canvas: payload })
    } catch (err) {
      console.warn('[canvas v2] persist failed', err?.message || err)
    }
  }, 800)
}

export function getCanvasV2() {
  return rootState(this)?.canvas
}

export function isCanvasV2() {
  return !!this.call('getCanvasScope')?.canvasV2
}

export function getCanvasItem(key) {
  return this.call('getCanvasV2')?.items?.[key]
}

export function getCanvasSelection() {
  return this.call('getCanvasV2')?.selection || { ids: [], primary: null }
}

export function getCanvasMode() {
  return this.call('getCanvasV2')?.mode || 'default'
}

export function setCanvasV2Mode(mode) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  v2.mode = mode
  const scope = this.call('getCanvasScope')
  if (scope) {
    scope.defaultMode = mode === 'default'
    scope.inspectMode = mode === 'inspect'
    scope.panningMode = mode === 'pan'
  }
  // Re-render the modes bar + inspect overlay (uses reactive `show:`).
  this.call('patchCanvas', { mode })
  this.call('refreshCanvasChrome')
}

export function toggleCanvasSettings() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  this.call('patchCanvas', { settingsOpen: !v2.settingsOpen })
}

export function setCanvasSettings(open) {
  this.call('patchCanvas', { settingsOpen: !!open })
}

// Viewport mutates in place — the scene transform is driven imperatively
// via `applyViewport`, so reactive re-render is unnecessary.
export function setViewport(patch) {
  const canvas = rootState(this)?.canvas
  if (!canvas) return
  canvas.viewport = { ...(canvas.viewport || {}), ...patch }
}

// Canonical "apply a viewport change": persist + scene transform +
// grid-bg refresh + mirror to v1 channels + reposition screen-space
// chrome (selection, handles, marquee, navbar, guides, snap lines).
export function applyViewport(viewport) {
  this.call('setViewport', viewport)
  const scope = this.call('getCanvasScope')
  if (scope) scope.positions = { ...scope.positions, x: viewport.x, y: viewport.y, scale: viewport.scale }
  try { resolveTopWindow().__cvViewport = { x: viewport.x, y: viewport.y, scale: viewport.scale } } catch {}
  const root = this.getRoot?.()
  const scene = root?.lookdown?.('CvScene')
  if (scene?.update) {
    scene.update({ style: { transform: `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${viewport.scale})` } })
  }
  const grid = root?.lookdown?.('CvGrid')
  if (grid?.update) {
    const size = 20 * (viewport.scale || 1)
    const px = ((viewport.x % size) + size) % size
    const py = ((viewport.y % size) + size) % size
    grid.update({ style: { backgroundSize: `${size}px ${size}px`, backgroundPosition: `${px}px ${py}px` } })
  }
  this.call('refreshCanvasChrome')
  this.call('syncCanvas')
}

// --- coord conversions --------------------------------------------------
// Canvas ↔ screen. Screen coords are relative to the canvas iframe's
// viewport (document window). CanvasEditor shares the same doc, so
// using `window.innerWidth/innerHeight` for center math is correct.
export function canvasToScreen(cx, cy) {
  const vp = this.call('getCanvasV2')?.viewport || { x: 0, y: 0, scale: 1 }
  return { x: cx * vp.scale + vp.x, y: cy * vp.scale + vp.y }
}

export function screenToCanvas(sx, sy) {
  const vp = this.call('getCanvasV2')?.viewport || { x: 0, y: 0, scale: 1 }
  return { x: (sx - vp.x) / vp.scale, y: (sy - vp.y) / vp.scale }
}

// --- chrome updater -----------------------------------------------------
// rAF-coalesces: callers tag the chrome dirty; the actual layout work
// runs once per browser frame even if called 100×/frame during gesture.
// Cache last applied geom per element so unchanged frames skip the
// `el.update` (a no-op write still allocates DOMQL diff overhead).

let _chromeRafId = null
let _chromeLastEl = null
const _lastGeom = new WeakMap()

function geomChanged(el, next) {
  const prev = _lastGeom.get(el)
  if (!prev) { _lastGeom.set(el, next); return true }
  for (const k in next) if (next[k] !== prev[k]) { _lastGeom.set(el, next); return true }
  return false
}

export function refreshCanvasChrome() {
  _chromeLastEl = this
  if (_chromeRafId) return
  const raf = (typeof requestAnimationFrame !== 'undefined') ? requestAnimationFrame : (cb) => setTimeout(cb, 16)
  _chromeRafId = raf(() => {
    _chromeRafId = null
    try { refreshCanvasChromeNow.call(_chromeLastEl) }
    catch (err) { console.error('[canvas v2] refreshCanvasChrome error', err) }
  })
}

function refreshCanvasChromeNow() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const root = this.getRoot?.(); if (!root) return
  const overlay = root.lookdown?.('CanvasOverlayV2'); if (!overlay) return
  const el = this
  const vp = v2.viewport

  // Selection box + handles
  const box = overlay.CvSelectionBox
  const ids = v2.selection.ids
  if (ids.length && box?.update) {
    const bbox = el.call('bboxForSelection')
    if (bbox) {
      const x = bbox.x * vp.scale + vp.x
      const y = bbox.y * vp.scale + vp.y
      const w = bbox.w * vp.scale
      const h = bbox.h * vp.scale
      box.update({ style: { display: 'block', left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px' } })

      // Handles — only for single selection
      const showHandles = ids.length === 1
      const handlePoints = [
        [HANDLE_NAMES[0], x, y], [HANDLE_NAMES[1], x + w / 2, y],
        [HANDLE_NAMES[2], x + w, y], [HANDLE_NAMES[3], x + w, y + h / 2],
        [HANDLE_NAMES[4], x + w, y + h], [HANDLE_NAMES[5], x + w / 2, y + h],
        [HANDLE_NAMES[6], x, y + h], [HANDLE_NAMES[7], x, y + h / 2]
      ]
      for (const [name, hx, hy] of handlePoints) {
        const h = overlay[name]
        if (!h?.update) continue
        const next = { display: showHandles ? 'block' : 'none', left: hx, top: hy }
        if (!geomChanged(h, next)) continue
        h.update({ style: {
          display: next.display,
          left: hx + 'px', top: hy + 'px',
          transform: 'translate(-50%, -50%)'
        } })
      }

      // SelectedNavbar host — only when exactly 1 selected
      const host = overlay.CvSelectedHost
      if (host?.update) {
        host.update({ style: showHandles
          ? { display: 'block', left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px' }
          : { display: 'none' } })
      }
    }
  } else {
    box?.update?.({ style: { display: 'none' } })
    for (const name of HANDLE_NAMES) overlay[name]?.update?.({ style: { display: 'none' } })
    overlay.CvSelectedHost?.update?.({ style: { display: 'none' } })
  }

  // Marquee
  const marq = overlay.CvDragAreaClip?.CvMarquee || overlay.CvMarquee
  if (marq?.update) {
    const m = v2.marquee
    if (m) {
      marq.update({ style: {
        display: 'block',
        left: (m.x * vp.scale + vp.x) + 'px',
        top: (m.y * vp.scale + vp.y) + 'px',
        width: (m.w * vp.scale) + 'px',
        height: (m.h * vp.scale) + 'px'
      } })
    } else {
      marq.update({ style: { display: 'none' } })
    }
  }

  // Connector arrows — render as bezier paths in CvConnectors SVG.
  const conn = overlay.CvConnectors
  const connNode = conn?.node
  if (connNode) {
    const list = v2.connections || []
    const dom = connNode.ownerDocument
    while (connNode.firstChild) connNode.removeChild(connNode.firstChild)
    if (list.length) {
      // Marker for arrowhead.
      const defs = dom.createElementNS('http://www.w3.org/2000/svg', 'defs')
      defs.innerHTML = '<marker id="cvArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="rgba(64,156,255,0.9)"/></marker>'
      connNode.appendChild(defs)
      for (const c of list) {
        const a = v2.items[c.from]; const b = v2.items[c.to]
        if (!a || !b) continue
        const ax = (a.x + a.w) * vp.scale + vp.x
        const ay = (a.y + a.h / 2) * vp.scale + vp.y
        const bx = b.x * vp.scale + vp.x
        const by = (b.y + b.h / 2) * vp.scale + vp.y
        const cx = (ax + bx) / 2
        const path = dom.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', `M${ax},${ay} C${cx},${ay} ${cx},${by} ${bx},${by}`)
        path.setAttribute('fill', 'none')
        path.setAttribute('stroke', 'rgba(64,156,255,0.85)')
        path.setAttribute('stroke-width', '2')
        path.setAttribute('marker-end', 'url(#cvArrow)')
        connNode.appendChild(path)
      }
    }
  }

  // Group dashed outlines — one screen-space rect per group.
  const groupsHost = overlay.CvGroupsLayer
  const groupsNode = groupsHost?.node
  if (groupsNode) {
    const groups = v2.groups || {}
    const existing = new Map()
    Array.from(groupsNode.children).forEach((c) => {
      const k = c.getAttribute('data-group-for')
      if (k) existing.set(k, c)
    })
    const seen = new Set()
    for (const [gid, g] of Object.entries(groups)) {
      seen.add(gid)
      const bbox = el.call('bboxForGroup', gid)
      if (!bbox) continue
      let box = existing.get(gid)
      if (!box) {
        box = groupsNode.ownerDocument.createElement('div')
        box.setAttribute('data-group-for', gid)
        box.style.position = 'absolute'
        box.style.border = '1px dashed rgba(64,156,255,0.85)'
        box.style.borderRadius = '4px'
        box.style.pointerEvents = 'none'
        groupsNode.appendChild(box)
      }
      const sx = bbox.x * vp.scale + vp.x
      const sy = bbox.y * vp.scale + vp.y
      box.style.left = (sx - 4) + 'px'
      box.style.top = (sy - 4) + 'px'
      box.style.width = (bbox.w * vp.scale + 8) + 'px'
      box.style.height = (bbox.h * vp.scale + 8) + 'px'
    }
    for (const [gid, box] of existing) if (!seen.has(gid)) box.remove()
  }

  // Minimap — render items + viewport rect in a 180x120 box.
  const minimap = overlay.CvMinimap
  const mmNode = minimap?.node
  if (mmNode) {
    const surf = root.lookdown?.('CanvasSurface')?.node
    const rect = surf?.getBoundingClientRect()
    let mmMinX = Infinity, mmMinY = Infinity, mmMaxX = -Infinity, mmMaxY = -Infinity
    for (const it of Object.values(v2.items)) {
      if (it.x < mmMinX) mmMinX = it.x
      if (it.y < mmMinY) mmMinY = it.y
      if (it.x + it.w > mmMaxX) mmMaxX = it.x + it.w
      if (it.y + it.h > mmMaxY) mmMaxY = it.y + it.h
    }
    if (Number.isFinite(mmMinX) && rect) {
      const PAD = 8
      const W = mmNode.clientWidth - PAD * 2
      const H = mmNode.clientHeight - PAD * 2
      const cw = mmMaxX - mmMinX
      const ch = mmMaxY - mmMinY
      const mScale = Math.min(W / cw, H / ch)
      const ox = PAD - mmMinX * mScale
      const oy = PAD - mmMinY * mScale
      const dom = mmNode.ownerDocument
      const have = new Map()
      Array.from(mmNode.children).forEach((c) => {
        const k = c.getAttribute('data-mm-for')
        if (k) have.set(k, c)
      })
      const seen = new Set()
      for (const [key, it] of Object.entries(v2.items)) {
        seen.add(key)
        let dot = have.get(key)
        if (!dot) {
          dot = dom.createElement('div')
          dot.setAttribute('data-mm-for', key)
          dot.style.position = 'absolute'
          dot.style.background = 'rgba(255,255,255,0.6)'
          dot.style.borderRadius = '1px'
          mmNode.appendChild(dot)
        }
        dot.style.left = (it.x * mScale + ox) + 'px'
        dot.style.top = (it.y * mScale + oy) + 'px'
        dot.style.width = (it.w * mScale) + 'px'
        dot.style.height = (it.h * mScale) + 'px'
      }
      for (const [key, dot] of have) if (!seen.has(key) && key !== '__viewport__') dot.remove()

      // Viewport rect in canvas-space: [-vp.x/scale, -vp.y/scale] sized [rect.w/scale, rect.h/scale]
      let vpEl = have.get('__viewport__')
      if (!vpEl) {
        vpEl = dom.createElement('div')
        vpEl.setAttribute('data-mm-for', '__viewport__')
        vpEl.style.position = 'absolute'
        vpEl.style.border = '1px solid rgba(64,156,255,0.95)'
        vpEl.style.background = 'rgba(64,156,255,0.15)'
        vpEl.style.pointerEvents = 'none'
        mmNode.appendChild(vpEl)
      }
      const vx = -vp.x / vp.scale
      const vy = -vp.y / vp.scale
      const vw = rect.width / vp.scale
      const vh = rect.height / vp.scale
      vpEl.style.left = (vx * mScale + ox) + 'px'
      vpEl.style.top = (vy * mScale + oy) + 'px'
      vpEl.style.width = (vw * mScale) + 'px'
      vpEl.style.height = (vh * mScale) + 'px'
    }
  }

  // Item titles — one screen-space label per item, positioned just above.
  const titlesHost = overlay.CvItemTitles
  const titlesNode = titlesHost?.node
  if (titlesNode) {
    const existing = new Map()
    Array.from(titlesNode.children).forEach((c) => {
      const k = c.getAttribute('data-title-for')
      if (k) existing.set(k, c)
    })
    const seen = new Set()
    for (const [key, it] of Object.entries(v2.items)) {
      seen.add(key)
      let lbl = existing.get(key)
      if (!lbl) {
        lbl = titlesNode.ownerDocument.createElement('div')
        lbl.setAttribute('data-title-for', key)
        lbl.style.position = 'absolute'
        lbl.style.color = 'white'
        lbl.style.fontSize = '11px'
        lbl.style.fontWeight = '500'
        lbl.style.padding = '2px 6px'
        lbl.style.background = 'rgba(0,0,0,0.6)'
        lbl.style.borderRadius = '3px'
        lbl.style.transform = 'translateY(-100%)'
        lbl.style.marginTop = '-4px'
        lbl.style.pointerEvents = 'none'
        lbl.style.whiteSpace = 'nowrap'
        lbl.textContent = key
        titlesNode.appendChild(lbl)
      }
      const sx = it.x * vp.scale + vp.x
      const sy = it.y * vp.scale + vp.y
      lbl.style.left = sx + 'px'
      lbl.style.top = sy + 'px'
    }
    // Remove labels for items that no longer exist.
    for (const [key, lbl] of existing) {
      if (!seen.has(key)) lbl.remove()
    }
  }
}

export function resetCanvasView() {
  this.call('applyViewport', { x: 40, y: 40, scale: 0.35 })
}

export function fitToSelection() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const ids = v2.selection.ids
  const bbox = ids.length
    ? this.call('bboxForSelection')
    : (() => {
        const all = Object.keys(v2.items)
        if (!all.length) return null
        return this.call('bboxForGroup', '__all__') || (() => {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
          for (const it of Object.values(v2.items)) {
            if (it.x < minX) minX = it.x; if (it.y < minY) minY = it.y
            if (it.x + it.w > maxX) maxX = it.x + it.w
            if (it.y + it.h > maxY) maxY = it.y + it.h
          }
          return Number.isFinite(minX) ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY } : null
        })()
      })()
  if (!bbox) return
  const root = this.getRoot?.()
  const surf = root?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect()
  if (!rect) return
  const PAD = 60
  const sx = (rect.width - 2 * PAD) / bbox.w
  const sy = (rect.height - 2 * PAD) / bbox.h
  const scale = Math.max(0.05, Math.min(2, Math.min(sx, sy)))
  const x = (rect.width - bbox.w * scale) / 2 - bbox.x * scale
  const y = (rect.height - bbox.h * scale) / 2 - bbox.y * scale
  this.call('applyViewport', { x, y, scale })
}

export function addConnection(from, to, label) {
  const v2 = this.call('getCanvasV2'); if (!v2 || !from || !to) return
  const next = [...(v2.connections || []), { from, to, label }]
  this.call('patchCanvas', { connections: next })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

export function removeConnection(from, to) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const next = (v2.connections || []).filter((c) => !(c.from === from && c.to === to))
  this.call('patchCanvas', { connections: next })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

export function toggleShortcutsOverlay() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  this.call('patchCanvas', { shortcutsOpen: !v2.shortcutsOpen })
}

// The eager panzoom shim lives in globalScope.js. It reads from
// `topWindow.__cvViewport`, which `applyViewport` writes on every
// viewport change — no second installation needed here.

export function initCanvasV2() {
  const root = rootState(this)
  // Restore previously persisted layout from the top-level `canvas`
  // field, with fallbacks for older saves. Only honour it if it
  // actually has items — a bare `canvas: {}` falls through to seed.
  const persistedRaw = root?.data?.canvas || root?.data?.canvasLayout || root?.data?.settings?.canvas
  const persisted = persistedRaw?.items && Object.keys(persistedRaw.items).length ? persistedRaw : null
  if (persisted && root?.update && !Object.keys(root.canvas?.items || {}).length) {
    const stored = loadStored()
    const base = DEFAULTS()
    if (stored.snap) base.snap = { ...base.snap, ...stored.snap }
    if (typeof stored.frameMode === 'boolean') base.frameMode = stored.frameMode
    root.update({
      canvas: {
        ...base,
        items: persisted.items || {},
        guides: persisted.guides || base.guides,
        groups: persisted.groups || {},
        viewport: persisted.viewport || base.viewport
      }
    })
    this.call('pushHistory')
    return
  }
  if (!root?.update) return
  if (root.canvas && Object.keys(root.canvas.items || {}).length) return

  const ctx = this.context || this.getRootContext?.() || {}
  const pages = ctx.projectPages || {}
  const entries = Object.entries(pages)

  // 4-col layout at 1722×1722 canvas-space px per thumbnail with 256 gap.
  const W = 1722
  const GAP = 256
  const COLS = 4
  const items = {}
  entries.forEach(([key, v], i) => {
    const col = i % COLS
    const row = Math.floor(i / COLS)
    const saved = v?.settings?.gridOptions || {}
    const usingSaved = saved.x != null && saved.y != null
    items[key] = {
      x: usingSaved ? saved.x * (W + GAP) / 3 : col * (W + GAP),
      y: usingSaved ? saved.y * (W + GAP) / 3 : row * (W + GAP),
      w: usingSaved ? (saved.w || 3) * (W / 3) : W,
      h: usingSaved ? (saved.h || 3) * (W / 3) : W,
      pageKey: key
    }
  })

  // Restore persisted snap + frameMode settings.
  const stored = loadStored()
  const base = DEFAULTS()
  if (stored.snap) base.snap = { ...base.snap, ...stored.snap }
  if (typeof stored.frameMode === 'boolean') base.frameMode = stored.frameMode
  if (typeof stored.showRulers === 'boolean') base.showRulers = stored.showRulers
  if (typeof stored.showMinimap === 'boolean') base.showMinimap = stored.showMinimap
  root.update({ canvas: { ...base, items } })
  this.call('pushHistory')
}
